import { FC, useRef } from 'react';

import {
    AiImageQueueType,
    BANNER_IMAGE_CROP_TYPES_CONFIG,
    BannerImageCropType,
    ImageComponent,
    IS_UPSCALE_TIP_WAS_SHOWN,
    UpscaleStatus,
} from 'utils/constants/banner';

import cropModalUpscaleButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/upscale/crop_modal_upscale_button_click';
import { Layer, Link, Tooltip, TooltipHover, VSpacingContainer } from '@hh.ru/magritte-ui';
import {
    ArrowPathOutlinedSize16,
    HyperstarSparkleFilledSize16,
    UturnLeftOutlinedSize16,
} from '@hh.ru/magritte-ui/icon';

import LocalStorageWrapper from 'bloko/common/storage/LocalStorageWrapper';

import useTrls from 'hooks/translation/useTrls';

import { fetchUpscale } from 'components/common/BannerImg/CropImageModal/fetchUpscale';

import {
    resetBannerImageToUpscaledByType,
    setBannerImageComponentByType,
    setBannerImageUpscaleStatusByType,
    setIsUpscaleTipVisible,
} from 'store/models/createBanner';
import { useDispatch, useSelector } from 'store/utils';

interface ImageActionsProps {
    cropType: BannerImageCropType;
    isSomeCropLoading: boolean;
    uploadNewImage: () => void;
    upscaleStatus?: UpscaleStatus;
}

const TrlKeys = {
    upscaleAndFillButton: 'BannerImg.CropImageModal.upscaleAndFill',
    upscaleAndFillTip: 'BannerImg.CropImageModal.upscaleAndFillTip',
    onlyOneActiveGenerationTip: 'BannerImg.CropImageModal.onlyOneActiveGenerationTip',
    reUpscaleAndFillButton: 'BannerImg.CropImageModal.reUpscaleAndFill',
    replaceButton: 'BannerImg.CropImageModal.replaceImage',
    resetButton: 'BannerImg.CropImageModal.reset',
};

const UPSCALE_OR_UPSCALE_AND_FILL = [UpscaleStatus.Upscale, UpscaleStatus.UpscaleAndFill];

export const ImageActions: FC<ImageActionsProps> = ({ cropType, isSomeCropLoading, uploadNewImage, upscaleStatus }) => {
    const { id: upscaledImageId, height, width } = useSelector((state) => state.createBanner.upscaledImages[cropType]);
    const isUpscaleTipVisible = useSelector((state) => state.createBanner.isUpscaleTipVisible);
    const currentImageId = useSelector((state) => state.createBanner.images[cropType].id);

    const isResetButtonVisible =
        upscaleStatus === UpscaleStatus.UpscaleAndFill || (upscaledImageId && upscaledImageId !== currentImageId);
    const isUpscaleTipWasShown = Boolean(LocalStorageWrapper.getItem(IS_UPSCALE_TIP_WAS_SHOWN));

    const cropAspect = BANNER_IMAGE_CROP_TYPES_CONFIG[cropType].aspect;
    const isImageAspectFitsCropAspect =
        height && width && cropAspect < width / height + 0.1 && cropAspect > width / height - 0.1;

    const trl = useTrls();
    const dispatch = useDispatch();

    const upscaleButtonRef = useRef<HTMLButtonElement>(null);

    const onUpscaleDropInfoClose = () => {
        dispatch(setIsUpscaleTipVisible(false));
        LocalStorageWrapper.setItem(IS_UPSCALE_TIP_WAS_SHOWN, 'true');
    };

    const upscaleAndFill = () => {
        if (isSomeCropLoading) {
            return;
        }

        onUpscaleDropInfoClose();

        cropModalUpscaleButtonClick({
            buttonName: upscaleStatus === UpscaleStatus.Upscale ? 'upscale_and_fill' : 'reupscale_and_fill',
            bannerImageCropType: cropType,
        });

        dispatch([
            setBannerImageComponentByType({
                cropType,
                component: ImageComponent.Loading,
            }),
            setBannerImageUpscaleStatusByType({
                cropType,
                upscaleStatus,
            }),
        ]);

        if (upscaledImageId) {
            void dispatch(fetchUpscale(upscaledImageId, cropType, AiImageQueueType.UpscaleAndFill));
        }
    };

    const resetImageToUpscaled = () => {
        cropModalUpscaleButtonClick({
            buttonName: 'reset_to_upscaled',
            bannerImageCropType: cropType,
        });

        dispatch(resetBannerImageToUpscaledByType(cropType));
    };

    return (
        <VSpacingContainer default={12}>
            <Link
                Element="button"
                iconLeft={<ArrowPathOutlinedSize16 />}
                onClick={uploadNewImage}
                disabled={isSomeCropLoading}
                data-qa="crop-image-replace"
            >
                {trl(TrlKeys.replaceButton)}
            </Link>
            {cropType !== BannerImageCropType.HH && upscaleStatus && (
                <>
                    {UPSCALE_OR_UPSCALE_AND_FILL.includes(upscaleStatus) && !isImageAspectFitsCropAspect && (
                        <>
                            <Link
                                ref={upscaleButtonRef}
                                Element="button"
                                iconLeft={<HyperstarSparkleFilledSize16 initialColor="negative" />}
                                disabled={isSomeCropLoading}
                                onClick={upscaleAndFill}
                                data-qa="crop-image-upscale_and_fill"
                            >
                                {trl(TrlKeys.upscaleAndFillButton)}
                            </Link>
                            <Layer layer="modal">
                                {(!isUpscaleTipVisible || isUpscaleTipWasShown) && (
                                    <TooltipHover placement="top-center" activatorRef={upscaleButtonRef}>
                                        {isSomeCropLoading
                                            ? trl(TrlKeys.onlyOneActiveGenerationTip)
                                            : trl(TrlKeys.upscaleAndFillTip)}
                                    </TooltipHover>
                                )}
                                <Tooltip
                                    visible={
                                        isUpscaleTipVisible &&
                                        !isUpscaleTipWasShown &&
                                        cropType === BannerImageCropType.YandexAndVK
                                    }
                                    activatorRef={upscaleButtonRef}
                                    placement="top-center"
                                    closeByClickOutside={false}
                                    showClose
                                    onClose={onUpscaleDropInfoClose}
                                >
                                    {trl(TrlKeys.upscaleAndFillTip)}
                                </Tooltip>
                            </Layer>
                        </>
                    )}
                    {isResetButtonVisible && (
                        <Link
                            Element="button"
                            iconLeft={<UturnLeftOutlinedSize16 />}
                            onClick={resetImageToUpscaled}
                            disabled={isSomeCropLoading}
                            data-qa="upscale-reset-button"
                        >
                            {trl(TrlKeys.resetButton)}
                        </Link>
                    )}
                </>
            )}
        </VSpacingContainer>
    );
};
